import { INavLink } from "@fluentui/react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { SidebarSettingsProps } from "../types/Sidebar.types";

import { SidebarMenuExpandable } from "./SidebarMenuExpandable";
import { SidebarFooter } from "./SidebarMenuFooter";
import { SidebarHeader } from "./SidebarMenuHeader";
import { SidebarMain } from "./SidebarMenuMain";
import { SidebarMenuPanel } from "./SidebarMenuPanel";

import {
  PermissionsAction,
  PermissionsAdminModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { useGetProfile } from "@/core/libs/api/react-query/index";

export function SidebarSettings({ theme }: SidebarSettingsProps): JSX.Element {
  const [linksEnabled, setLinksEnabled] = useState<INavLink[] | []>([]);
  const location = useLocation();

  const userProfile = useGetProfile();
  const permissions = userProfile.data?.data?.role.permissions;

  const footerLinks: INavLink[] = [
    {
      name: "Painel de benefícios compartilhados",
      url: "/",
      key: "/",
      iconName: "ViewDashboard",
      iconOnly: true
    }
  ];

  useEffect(() => {
    const makingConfigurationLinks = {
      name: "Configurações",
      url: "/settings",
      links: [],
      isExpanded: location.pathname.startsWith("/settings"),
      level: 1
    };

    const makingConfigurationGlobalLinks = {
      name: "Configurações Globais",
      url: "/settings",
      links: [],
      isExpanded: location.pathname.startsWith("/settings"),
      level: 1
    };

    const makingLinesLinks = {
      name: "Linhas",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingContractLinks = {
      name: "Contratos",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingInventoriesGlobalLinks = {
      name: "Estoque Global",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingBusinessUnitLinks = {
      name: "Unidades de Negócio",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingInventoriesLinks = {
      name: "Estoque",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingProductsLinks = {
      name: "Benefícios combos",
      url: "/settings",
      links: [],
      isExpanded: location.pathname.startsWith("/settings")
    };

    const makingOffersLinks = {
      name: "Pacotes de Benefícios",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const makingAdditionalServiceLinks = {
      name: "Atividades de valor agregado",
      url: "/settings",
      links: [],
      isExpanded: false,
      level: 2
    };

    const allLinksEnabled: INavLink[] = [];

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.LINES]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingLinesLinks.links.push({
        name: "Linhas",
        url: "/settings/lines",
        key: "/settings/lines",
        iconName: "CellPhone",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.LINES_OCCURRENCES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingLinesLinks.links.push({
        name: "Ocorrências de Linhas",
        url: "/settings/lines-logs",
        key: "/settings/lines-logs",
        iconName: "ReadingMode",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.CARRIERS]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      permissions[PermissionsScope.ADMIN][PermissionsAdminModule.PLANS]?.some(
        action =>
          action === PermissionsAction.ALL || action === PermissionsAction.LIST
      ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Plano de Benefícios",
        url: "/settings/plans",
        key: "/settings/plans",
        iconName: "Communications",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.USERS]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationLinks.links.push({
        name: "Usuários",
        url: "/settings/users",
        key: "/settings/users",
        iconName: "People",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.SALES_TEAMS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Times comerciais",
        url: "/settings/sales-teams",
        key: "/settings/sales-teams",
        iconName: "TeamFavorite",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.BUSINESS_UNITS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingBusinessUnitLinks.links.push({
        name: "Unidades de negócio",
        url: "/settings/business-units",
        key: "/settings/business-units",
        iconName: "EMI",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.SHIPPINGS]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Precificação de Fretes",
        url: "/settings/shippings",
        key: "/settings/shippings",
        iconName: "DeliveryTruck",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.BUSINESS_UNITS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Prestadores de Benefício",
        url: "/settings/service-providers",
        key: "/settings/service-providers",
        iconName: "CRMServices",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.ELECTRONIC_SIGNATURES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Assinaturas Digitais",
        url: "/settings/electronic-signatures",
        key: "/settings/electronic-signatures",
        iconName: "Fingerprint",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.SERVICES]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Serviços",
        url: "/settings/services",
        key: "/settings/services",
        iconName: "TaskManager",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.SERVICES_CATEGORIES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Categorias de Benefícios prestados",
        url: "/settings/services-categories",
        key: "/settings/services-categories",
        iconName: "ViewListGroup",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.OPERATIONS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Operações",
        url: "/settings/operations",
        key: "/settings/operations",
        iconName: "Processing",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.PERMISSIONS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Permissões",
        url: "/settings/permissions",
        key: "/settings/permissions",
        iconName: "Permissions",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.ROLES]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Cargos",
        url: "/settings/roles",
        key: "/settings/roles",
        iconName: "ManagerSelfService",
        level: 2
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.EMAIL_TEMPLATE_GROUP
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingConfigurationGlobalLinks.links.push({
        name: "Grupo de modelos de email",
        url: "/settings/email-template-group",
        key: "/settings/email-template-group",
        iconName: "mail",
        level: 2
      });
    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.CONTRACTS_MODELS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingContractLinks.links.push({
        name: "Modelos de Contrato",
        url: "/settings/models",
        key: "/settings/models",
        iconName: "OpenEnrollment",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.SERVICE_MODEL_OPTIONS_GROUP
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingContractLinks.links.push({
        name: "Grupos de Modelos de Contrato",
        url: "/settings/models-groups",
        key: "/settings/models-groups",
        iconName: "GroupList",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.CONTRACTS_MODELS_ATTACHMENTS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingContractLinks.links.push({
        name: "Anexos",
        url: "/settings/models-attachments",
        key: "/settings/models-attachments",
        iconName: "GroupList",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORIES_DISTRIBUTION_CENTERS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesLinks.links.push({
        name: "Centros de distribuição primários",
        url: "/settings/primary-distribution-centers",
        key: "/settings/primary-distribution-centers",
        iconName: "ProductionFloorManagement",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORIES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.READ
    ) &&
      makingInventoriesLinks.links.push({
        name: "Verificação de Elegibilidade",
        url: "/settings/inventory-eligibility",
        key: "/settings/inventory-eligibility",
        iconName: "ReceiptCheck",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORIES_DISTRIBUTION_CENTERS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesLinks.links.push({
        name: "Centros de distribuição secundários",
        url: "/settings/secondary-distribution-centers",
        key: "/settings/secondary-distribution-centers",
        iconName: "ProductionFloorManagement",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.PRODUCTS]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesGlobalLinks.links.push({
        name: "Produtos",
        url: "/settings/products",
        key: "/settings/products",
        iconName: "ProductVariant",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.PRODUCTS_BRANDS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesGlobalLinks.links.push({
        name: "Marcas",
        url: "/settings/brands",
        key: "/settings/brands",
        iconName: "Ticket",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.PRODUCTS_CATEGORIES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesGlobalLinks.links.push({
        name: "Categorias",
        url: "/settings/products-categories",
        key: "/settings/products-categories",
        iconName: "ProductList",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORIES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesLinks.links.push({
        name: "Inventário",
        url: "/settings/inventories",
        key: "/settings/inventories",
        iconName: "ProductCatalog",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORY_MOVEMENTS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesLinks.links.push({
        name: "Movimentações de Inventário",
        url: "/settings/inventory-movements",
        key: "/settings/inventory-movements",
        iconName: "DoubleChevronRight8",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.INVENTORIES_OCCURRENCES
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingInventoriesLinks.links.push({
        name: "Ocorrências de Inventário",
        url: "/settings/inventories-occurrences",
        key: "/settings/inventories-occurrences",
        iconName: "ReadingMode",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][PermissionsAdminModule.OFFERS]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingOffersLinks.links.push({
        name: "Ofertas",
        url: "/settings/offers",
        key: "/settings/offers",
        iconName: "Coupon",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.OFFERS_BANNERS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingOffersLinks.links.push({
        name: "Banners",
        url: "/settings/banners",
        key: "/settings/banners",
        iconName: "ImageDiff",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.PRICING_GROUPS
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingOffersLinks.links.push({
        name: "Grupos de Precificação",
        url: "/settings/pricing-groups",
        key: "/settings/pricing-groups",
        iconName: "money",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.ADDITIONAL
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingAdditionalServiceLinks.links.push({
        name: "Atividades de valor agregado",
        url: "/settings/additional",
        key: "/settings/additional",
        iconName: "AppIconDefaultAdd",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.ADDITIONAL_CATEGORY
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingAdditionalServiceLinks.links.push({
        name: "Categorias de Atividades de valor agregado",
        url: "/settings/additional-category",
        key: "/settings/additional-category",
        iconName: "AppIconDefaultList",
        level: 3
      });

    permissions[PermissionsScope.ADMIN][
      PermissionsAdminModule.BUSINESS_UNIT_CLASSIFICATION
    ]?.some(
      action =>
        action === PermissionsAction.ALL || action === PermissionsAction.LIST
    ) &&
      makingBusinessUnitLinks.links.push({
        name: "Classificação de Unidades de Negócio",
        url: "/settings/business-unit-classification",
        key: "/settings/business-unit-classification",
        iconName: "Manufacturing",
        level: 3
      });

    if (makingLinesLinks.links.length > 0) {
      makingConfigurationLinks.links.push({
        ...makingLinesLinks,
        links: makingLinesLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          return 0;
        })
      });
    }

    if (makingAdditionalServiceLinks.links.length > 0) {
      makingConfigurationGlobalLinks.links.push({
        ...makingAdditionalServiceLinks,
        links: makingAdditionalServiceLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          // a must be equal to b
          return 0;
        })
      });
    }

    if (makingOffersLinks.links.length > 0) {
      makingConfigurationGlobalLinks.links.push({
        ...makingOffersLinks,
        links: makingOffersLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          // a must be equal to b
          return 0;
        })
      });
    }

    if (makingContractLinks.links.length > 0) {
      makingConfigurationGlobalLinks.links.push({
        ...makingContractLinks,
        links: makingContractLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          // a must be equal to b
          return 0;
        })
      });
    }

    if (makingInventoriesLinks.links.length > 0) {
      makingConfigurationLinks.links.push({
        ...makingInventoriesLinks,
        links: makingInventoriesLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          return 0;
        })
      });
    }

    if (makingBusinessUnitLinks.links.length > 0) {
      makingConfigurationLinks.links.push({
        ...makingBusinessUnitLinks,
        links: makingBusinessUnitLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          return 0;
        })
      });
    }

    if (makingInventoriesGlobalLinks.links.length > 0) {
      makingConfigurationGlobalLinks.links.push({
        ...makingInventoriesGlobalLinks,
        links: makingInventoriesGlobalLinks.links.sort((a, b) => {
          if (a.name > b.name) {
            return 1;
          }

          if (a.name < b.name) {
            return -1;
          }

          return 0;
        })
      });
    }

    if (makingProductsLinks.links.length > 0) {
      allLinksEnabled.push(makingProductsLinks);
    }

    if (makingConfigurationLinks.links.length > 0) {
      allLinksEnabled.push({
        ...makingConfigurationLinks,
        links: makingConfigurationLinks.links
          .sort((a, b) => {
            if (a.name > b.name) {
              return 1;
            }

            if (a.name < b.name) {
              return -1;
            }

            return 0;
          })
          .sort((a, b) => {
            if (a.links && a.links.length > 0) {
              return -1;
            }

            return 1;
          })
      });
    }

    if (makingConfigurationGlobalLinks.links.length > 0) {
      allLinksEnabled.push({
        ...makingConfigurationGlobalLinks,
        links: makingConfigurationGlobalLinks.links
          .sort((a, b) => {
            if (a.name > b.name) {
              return 1;
            }

            if (a.name < b.name) {
              return -1;
            }

            return 0;
          })
          .sort((a, b) => {
            if (a.links && a.links.length > 0) {
              return -1;
            }

            return 1;
          })
      });
    }
    setLinksEnabled(allLinksEnabled);
    // eslint-disable-next-line
  }, [permissions]);

  return (
    <>
      <SidebarMenuPanel
        HeaderContent={SidebarHeader}
        MainContent={SidebarMain}
        mainLinks={linksEnabled}
        FooterContent={SidebarFooter}
        footerLinks={footerLinks}
        theme={theme}
      />

      <SidebarMenuExpandable
        theme={theme}
        FooterContent={SidebarFooter}
        MainContent={SidebarMain}
        footerLinks={footerLinks}
        mainLinks={linksEnabled}
        maxPathNameRange={3}
      />
    </>
  );
}
